import type { MouseEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalNodeSelection } from '@lexical/react/useLexicalNodeSelection';
import { mergeRegister } from '@lexical/utils';
import {
  $applyNodeReplacement,
  $getNodeByKey,
  CLICK_COMMAND,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  DOMExportOutput,
  DRAGSTART_COMMAND,
  EditorConfig,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  NodeKey,
  SerializedLexicalNode,
  Spread,
} from 'lexical';

export type SerializedAudioNode = Spread<
  {
    src: string;
    type: 'audio';
    version: 1;
  },
  SerializedLexicalNode
>;

export class AudioNode extends DecoratorNode<JSX.Element> {
  __src: string;

  static getType(): string {
    return 'audio';
  }

  static clone(node: AudioNode): AudioNode {
    return new AudioNode(node.__src, node.__key);
  }

  constructor(src: string, key?: NodeKey) {
    super(key);
    this.__src = src;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    const theme = config.theme;
    const className = theme.audio;
    if (className) {
      div.className = className;
    }
    return div;
  }

  updateDOM(): false {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('audio');
    element.src = this.__src;
    element.controls = true;
    return { element };
  }

  setSource(src: string): void {
    const writable = this.getWritable();
    writable.__src = src;
  }

  decorate(): JSX.Element {
    return <AudioComponent nodeKey={this.getKey()} src={this.__src} />;
  }

  static importJSON(serializedNode: SerializedAudioNode): AudioNode {
    const node = $createAudioNode(serializedNode.src);
    return node;
  }

  exportJSON(): SerializedAudioNode {
    return {
      ...super.exportJSON(),
      src: this.__src,
      type: 'audio',
      version: 1,
    };
  }
}

export function $createAudioNode(src: string): AudioNode {
  return $applyNodeReplacement(new AudioNode(src));
}

function getAudioMimeType(src: string): string {
  const extension = src.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'mp3':
      return 'audio/mpeg';
    case 'wav':
      return 'audio/wav';
    case 'ogg':
      return 'audio/ogg';
    case 'aac':
      return 'audio/aac';
    default:
      // Fallback for unknown types, browser will attempt to figure it out
      return 'audio/mpeg';
  }
}

function AudioComponent({ src, nodeKey }: { src: string; nodeKey: string }) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(nodeKey);
  const audioRef = useRef<HTMLAudioElement>(null);

  const onDelete = useCallback(
    (event: KeyboardEvent) => {
      if (isSelected) {
        event.preventDefault();
        editor.update(() => {
          const node = $getNodeByKey(nodeKey);
          if (node) {
            node.remove();
          }
        });
        return true;
      }
      return false;
    },
    [editor, isSelected, nodeKey],
  );

  const onClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === audioRef.current) {
        event.preventDefault();
        if (event.shiftKey) {
          setSelected(!isSelected);
        } else {
          clearSelection();
          setSelected(true);
        }
        return true;
      }
      return false;
    },
    [clearSelection, setSelected, isSelected],
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.registerCommand(CLICK_COMMAND, onClick, COMMAND_PRIORITY_LOW),
      editor.registerCommand(
        DRAGSTART_COMMAND,
        (event) => {
          if (event.target === audioRef.current) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(KEY_DELETE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
      editor.registerCommand(KEY_BACKSPACE_COMMAND, onDelete, COMMAND_PRIORITY_LOW),
    );

    return unregister;
  }, [editor, onClick, onDelete]);

  return (
    <div className="relative w-full my-8 flex justify-center">
      <div
        aria-label="Audio"
        className={`w-[60%] max-w-3xl relative cursor-pointer shadow-01 hover:shadow-02 ${
          isSelected ? 'ring-2 ring-blue-500 ring-offset-2' : ''
        }`}
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick(e as unknown as MouseEvent);
          }
        }}>
        <audio
          ref={audioRef}
          controls
          className="w-full rounded-lg object-cover shadow-lg hover:shadow-xl">
          <source src={src} type={getAudioMimeType(src)} />
          Your browser does not support the audio tag.
        </audio>
      </div>
    </div>
  );
}
